import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
// import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import EventBus from '../../../../../utilities/event-bus';
import { Priority, Progress } from '../../../enums';
import { TaskStatus } from '../../debrief/enum';
import { formatDisplayDate } from '@/utilities/date-formatter';
import { getAllProgress } from '../../../constants';
import { getAllPriorities } from '../../../constants';
import { getFullName } from '@/utilities';
import { getOrderForHttp, getPage, getPerPage } from '@/composables/PaginationComposable';
import { companyStore } from '@/store/global-stores';
import { useLabelRenameStore } from '@/modules/label-rename/store';

const TASK_URL = process.env.VUE_APP_API_VERSION + '/tasks';
export const useTaskStore = defineStore('task', () => {
    const authStore = useAuthStore();
    const labelRenameStore = useLabelRenameStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    const loggedUser = computed(() => {
        return authStore.loggedUserId;
    });
    const isFeatureEnabled = companyStore.useFeatureAccess('assurance-level');

    //state
    const task = ref({
        company_id: null,
        learning_outcome_id: null,
        from_department_id: null,
        to_department_id: null,
        source_id: null,
        theme_id: null,
        assigned_to: [],
        notified_to: [],
        title: null,
        linked_tasks: null,
        linked_tasks_details: [], // For UI only
        priority: Priority.MEDIUM, // Medium by default
        progress_status: TaskStatus.NOPROGRESS, //No progress by default
        start_date: null,
        due_date: null,
        reminder_date: null,
        description: null,
        files_attached: [],
        draft: 0,
        assurance_level: null,
        archived_at: null,
        unarchived_at: null,
        author_id:null
    });

    const existing_tasks = ref([]);
    const draft_tasks = ref([]);
    const params = ref({
        rows: 10,
        page: null,
        sortingInfo: 'id desc',
        sortField: null,
        sortOrder: null,
    });
    const loading = ref(false);
    const search = ref({
        title: null,
        progress: [Progress.NO_PROGRESS, Progress.IN_PROGRESS, Progress.COMPLETED],
        assigned_to: [],
        department_to: null,
        source: null,
        theme: null,
        priority: null,
        with_learning_outcome: null,
        debrief: null,
        overdue: null,
        start_date_from: null,
        start_date_to: null,
        due_date_from: null,
        due_date_to: null,
        author: [],
        assurance_level: null,
    });

    const notification = ref({
        subject: null,
        message: null,
        tasks: [],
    });

    const totalRecords = ref(null);
    const allProgress = ref(getAllProgress());
    const allPriorities = ref(getAllPriorities());
    const editId = ref(null);
    const draftTaskDetails = ref(null);

    let assuranceLevelStatus = ref(null);

    // getters
    const allTasks = computed(() => {
        return existing_tasks.value?.map((task) => {
            return {
                ...task,
                created_at: formatDisplayDate(task?.created_at),
                due_date_display: formatDisplayDate(task?.due_date),
                task_progress: allProgress.value.find((status) => status.id == task.progress),
                task_priority: allPriorities.value.find((priority) => priority.id == task.priority)?.name,
                assigned_user: task?.users_assigned_to
                    .map((obj) => {
                        return getFullName(obj.user);
                    })
                    .join(', '),
                assigned_by: getFullName(task.author),
                theme_name: task.theme?.name,
            };
        });
    });

    const editMode = computed(() => {
        return editId.value ? true : false;
    });

    const taskFormHeader = computed(() => {
        const taskLabel = labelRenameStore.getLabel('Task', 'Task');
        return editMode.value ? `Edit ${taskLabel}` : `Add ${taskLabel}`;
    });

    //actions
    //Fetch all Non Draft Tasks
    const getExistingTasks = async (list) => {
        try {


            // NProgress.start();
            loading.value = true;
            existing_tasks.value = [];
            //let page_size = params.value.rows ?? '';
            let page_size = getPerPage(list);
            //let page = params.value.page ?? '';
            let page = getPage(list);
            const sortOrder = getOrderForHttp(list)

            const response = await authApi.get(TASK_URL, {
                params: {
                    company_id: company_id.value,
                    ...search.value,
                    with_learning_outcome: search.value?.with_learning_outcome === 'Both' ? null : search.value?.with_learning_outcome,
                    page_size: page_size,
                    page: page + 1,
                    sort: sortOrder,
                },
            });
            if (response.statusCode === 200) {
                const data = response.data;

                existing_tasks.value = data.data;
                totalRecords.value = data.total;
            }
        } catch (error) {
            console.error("fail")
            showToast({}, error?.data?.message ?? 'Failed to fetch existing Tasks');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };

    // For tasks lists on Landing Page
    const getNonDraftTasks = async (list, params) => {

        let apiParam = {
            page_size: getPerPage(list),
            assigned_to: params.assigned_to,
            page: getPage(list),
            rows: getPerPage(list),
            sort: getOrderForHttp(list),
            company_id: company_id.value,
        }

        if (list === 'near-due-date' || list === 'latest-tasks' || list === 'over-due-date') {
            apiParam.sort = params.sort

            if (typeof params.overdue !== 'undefined') {
                apiParam.overdue = params.overdue;
            }
        }


        try {
            const res = await authApi.get(TASK_URL, {
                params: apiParam,
            });
            if (res.statusCode === 200) {
                let tasks = res.data.data;
                let totalRecords = res.data.total;
                if (totalRecords > 0) {
                    tasks = tasks.map((task) => {
                        return {
                            ...task,
                            due_at: formatDisplayDate(task?.due_date),
                            task_progress: allProgress.value.find((status) => status.id == task.progress),
                            task_priority: allPriorities.value.find((priority) => priority.id == task.priority),
                            assigned_user: task?.users_assigned_to
                                .map((obj) => {
                                    return getFullName(obj.user);
                                })
                                .join(', '),
                            assigned_by: getFullName(task.author),
                            theme_name: task.theme?.name,
                        };
                    });
                }
                return {
                    data: tasks,
                    total: totalRecords,
                };
            }
            return {};
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch non draft tasks');
        }

    };

    const getDraftTasks = async () => {
        try {
            const response = await authApi.get(`${TASK_URL}/draft`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                draft_tasks.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch draft Task');
        }
    };

    const createTask = async () => {
        try {
            const bodyParams = { ...task.value, company_id: company_id.value };
            const response = await authApi.post(TASK_URL, bodyParams);
            if (response.statusCode === 201) {
                task.value.draft == 0 ? showToast(response.toast, 'New Task created successfully') : showToast(response.toast, 'New Draft Task created successfully');
                initTask();
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create Task');
        }
    };

    const fetchTaskDetails = async (taskId) => {
        try {
            const response = await authApi.get(`${TASK_URL}/${taskId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                task.value.assurance_level = response.data.assurance_level ?? null;
                task.value.archived_at = response.data.archived_at ?? null;
                task.value.unarchived_at = response.data.unarchived_at ?? null;
                task.value.author_id = response.data.author_id ?? null;
                return response.data;
            }
        } catch (error) {
            console.error('Fetch task failed:', error);
            showToast({}, error?.data?.message ?? 'Failed to fetch details of Task with Id :' + taskId);
        }
    };

    const updateExistingTask = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...task.value, company_id: company_id.value };
            delete bodyParams.attachment;
            const response = await authApi.patch(`${TASK_URL}/${editId.value}`, bodyParams);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Task updated successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update Task');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const sendNotification = async () => {
        try {
            const bodyParams = { ...notification.value };
            const response = await authApi.post(`${TASK_URL}/notify?company_id=${company_id.value}`, bodyParams);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Notification sent successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to send notification');
        }
    };

    const deleteTask = async (taskId) => {
        try {
            const response = await authApi.delete(`${TASK_URL}/${taskId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                // remove the task from the tasks now
                const existingTaskIndex = existing_tasks.value.findIndex((task) => task.id === taskId);
                existing_tasks.value.splice(existingTaskIndex, 1);
                showToast(response.toast, 'Task deleted successfully');
                return true;
            }
        } catch (error) {
            // Check if there is any linked tasks which is causing errors
            const linkedTasks = error?.data?.data;
            // if yes then mention those task ids which is causing the issue
            if (linkedTasks?.length > 0) {
                const linkedTasksString = linkedTasks.join(', ');
                showToast({}, `Unable to delete the task because its linked with following task ids: ${linkedTasksString}`);
            }
            // Else just show the usual message
            else {
                showToast({}, error?.data?.message ?? 'Failed to delete task');
            }
        }
    };

    const switchTaskStatus = async (params) => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...params, company_id: company_id.value };

            // Status update is the primary operation: do it first so a failed
            // assurance PATCH can't prevent the status change. The assurance
            // PATCH is only attempted when the user actually picked a level —
            // sending null fails the backend's `required` rule and used to
            // throw inside the await, masking the real status response.
            const response = await authApi.post(`${TASK_URL}/status`, bodyParams);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Task status updated ');
                if (bodyParams.status == 1) {
                    task.value.assurance_level = null;
                    assuranceLevelStatus.value = null;
                } else if (isFeatureEnabled.value && assuranceLevelStatus.value != null) {
                    await onAssuranceLevelChange(assuranceLevelStatus.value, bodyParams.tasks?.[0]);
                }
                return true;
            }
        } catch (error) {
            console.error('Error switching task status:', error);
            showToast({}, error?.data?.message ?? ' Failed to update task status');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    }

    const onAssuranceLevelChange = async (assuranceLevel, taskId = null) => {
        // useRoute() returns undefined when this store is instantiated outside a
        // component setup (it's initialised via global-stores.js at module load),
        // so don't rely on `route.params.id`. Prefer an explicit taskId from the
        // caller, then fall back to the currently-loaded task in the store.
        const resolvedTaskId = taskId ?? task.value?.id;
        if (!resolvedTaskId) {
            console.error('onAssuranceLevelChange: no task id available');
            showToast({}, 'Failed to update assurance level');
            return false;
        }

        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.patch(`${TASK_URL}/${resolvedTaskId}/assurance-level`, {
                assurance_level: assuranceLevel,
                company_id: company_id.value, // only needed if user is a root user
            });

            if (response.statusCode === 200) {
                showToast(response.toast, 'Progress to completion updated successfully');
                task.value.assurance_level = response.data.assurance_level
                return true;
            }
        } catch (error) {
            console.error('Error updating assurance level:', error);
            showToast({}, error?.data?.message ?? 'Failed to update assurance level');
        } finally {
            EventBus.emit('hide-operation-loader');
        }

        return false;
    };

    const fetchTaskDelegationCandidates = async (taskId) => {
        try {
            const response = await authApi.get(`${TASK_URL}/delegate/${taskId}/candidates`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? ' Failed to fetch task delegation candidates');
        }
    };

    const delegateTask = async (taskId, fromToUsers) => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = {
                company_id: company_id.value,
                users: fromToUsers,
            };
            const response = await authApi.patch(`${TASK_URL}/delegate/${taskId}`, bodyParams);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Task delegated to new assignees successfully');
                return true;
            }
        } catch (error) {
            showToast({ severity: 'warn' }, 'Failed to delegate task to the new assignees');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const onArchiveTask = async (comment_id, archiveState, id = null) => {
        // Same caveat as onAssuranceLevelChange: `route` may be undefined here
        // when the store is created at module load via global-stores.js, so we
        // can't use it in a default-parameter expression. Fall back to the
        // currently-loaded task in the store.
        const taskId = id ?? task.value?.id;
        if (!taskId) {
            console.error('onArchiveTask: no task id available');
            showToast({}, 'Failed to archive task');
            return false;
        }
        try {
            EventBus.emit('show-operation-loader');

            const response = await authApi.patch(`${TASK_URL}/archive/${taskId}`, {
                comment_id: comment_id,
                type: archiveState.value,
                company_id: company_id.value, // include only if applicable for root users
            });
            if (response.statusCode === 200) {
                showToast(response.toast, 'Task archived successfully');

                // Optional: update local task state to reflect archive status
                task.value.archived_at = response.data.archived_at ?? null;
                task.value.unarchived_at = response.data.unarchived_at ?? null;
                return true;
            }
        } catch (error) {
            console.error('Error archiving task:', error);
            showToast({}, error?.data?.message ?? 'Failed to archive task');
        } finally {
            EventBus.emit('hide-operation-loader');
        }

        return false;
    };

    const isTaskOwnerOrSelf = (taskAuthorId) => {
        return loggedUser.value === taskAuthorId;
    };

    //helpers
    const initTask = () => {
        task.value.company_id = null;
        task.value.learning_outcome_id = null;
        task.value.from_department_id = null;
        task.value.to_department_id = null;
        task.value.source_id = null;
        task.value.theme_id = null;
        task.value.assigned_to = [];
        task.value.notified_to = [];
        task.value.title = null;
        task.value.linked_tasks = null;
        task.value.priority = Priority.MEDIUM; // Medium by default
        task.value.progress_status = TaskStatus.NOPROGRESS; //No progress by default
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        task.value.start_date = `${y}-${m}-${day}`;
        task.value.due_date = null;
        task.value.reminder_date = null;
        task.value.description = null;
        task.value.files_attached = [];
        task.value.draft = 0;
        task.value.archived_at = null;
        editId.value = null;
    };

    const initSearch = () => {
        search.value.title = null;
        search.value.progress = [Progress.NO_PROGRESS, Progress.IN_PROGRESS, Progress.COMPLETED];
        search.value.assigned_to = [];
        search.value.department_to = null;
        search.value.source = null;
        search.value.theme = null;
        search.value.priority = null;
        search.value.with_learning_outcome = null;
        search.value.debrief = null;
        search.value.overdue = null;
        search.value.start_date_from = null;
        search.value.start_date_to = null;
        search.value.due_date_from = null;
        search.value.due_date_to = null;
        search.value.author = []
        search.value.assurance_level = null;
    };

    const initparams = () => {
        params.value.rows = 10;
        params.value.sortField = null;
        params.value.sortOrder = null;
        params.value.page = null;
        params.value.sortingInfo = 'id desc';
    };

    const loadTaskBody = async (taskData) => {
        task.value.learning_outcome_id = taskData?.learning_outcome_id;
        task.value.from_department_id = taskData?.from_department_id;
        task.value.to_department_id = taskData?.to_department_id;
        task.value.source_id = taskData?.source_id;
        task.value.theme_id = taskData?.theme_id;
        task.value.assigned_to = taskData?.users_assigned_to ? taskData.users_assigned_to?.map((item) => item.user?.id) : [];
        task.value.notified_to = taskData?.users_notified ? taskData.users_notified?.map((item) => item.user?.id) : [];
        task.value.title = taskData?.title;
        task.value.linked_tasks = taskData?.linked_to?.map((item) => item.linked_to.id);
        task.value.linked_tasks_details = taskData?.linked_to ?? []; // to make further details
        task.value.priority = Number(taskData?.priority);
        task.value.progress_status = Number(taskData?.progress);
        task.value.start_date = taskData?.start_date;
        task.value.due_date = taskData?.due_date;
        task.value.reminder_date = taskData?.reminder_date;
        task.value.description = taskData?.description;
        task.value.attachment = taskData?.attachment;
        task.value.files_attached = taskData?.attachment?.map((item) => item.attachment_id);
        task.value.draft = taskData?.draft;
        task.value.archived_at = taskData?.archived_at;
        task.value.unarchived_at = taskData?.unarchived_at;
    };

    // for task details view
    const initNotification = () => {
        notification.value.subject = null;
        notification.value.message = null;
        notification.value.tasks = [];
    };

    const isArchived = computed(() => {
        const archivedAt = task.value.archived_at ? new Date(task.value.archived_at) : null;
        const unarchivedAt = task.value.unarchived_at ? new Date(task.value.unarchived_at) : null;

        if (!archivedAt) return false;
        if (!unarchivedAt) return true;

        return archivedAt > unarchivedAt;
    });

    return {
        //state
        task,
        existing_tasks,
        params,
        search,
        totalRecords,
        loading,
        editId,
        notification,
        draft_tasks,
        draftTaskDetails,
        assuranceLevelStatus,
        isTaskOwnerOrSelf,

        //getters
        allTasks,
        editMode,
        taskFormHeader,

        //actions
        getExistingTasks,
        createTask,
        fetchTaskDetails,
        updateExistingTask,
        sendNotification,
        getDraftTasks,
        deleteTask,
        switchTaskStatus,
        getNonDraftTasks,
        fetchTaskDelegationCandidates,
        delegateTask,
        onAssuranceLevelChange,
        onArchiveTask,

        //helpers
        initTask,
        initSearch,
        initparams,
        loadTaskBody,
        initNotification,

        isArchived,
    };
});
